/** Collects delivery-point and redemption data and uploads via the injected POST callback. */
import { ok, fail, type Result } from '@core/domain/utils/Result.ts';
import { AppConfig } from '@core/domain/AppConfig.ts';
import { createCorrelationId } from '@core/domain/utils/correlationId.ts';
import { buildOfflineSyncWirePayload } from './buildOfflineSyncWirePayload.ts';
import type {
  OfflineSyncPuntosEntregaExportPort,
  OfflineSyncRedencionExportPort,
} from '../domain/ports/offlineSyncExportPorts.ts';
import type {
  OfflineSyncExportBody,
  OfflineSyncPayload,
  OfflineSyncRedencionBundle,
  OfflineSyncUploadResult,
} from '../domain/offlineSyncPayload.ts';
import type { OfflineSyncPort, OfflineSyncProgress } from '../domain/ports/OfflineSyncPort.ts';
import type { OfflineUploadPersistBatch } from '../domain/offlineUploadPersist.ts';

import type { SyncPdfResolver } from '../domain/ports/SyncPdfResolver.ts';
import { formatSyncFailureUserMessage } from '../domain/syncTransportError.ts';
import {
  exceedsSyncUploadMaxPayloadBytes,
  SYNC_UPLOAD_PAYLOAD_TOO_LARGE_ES,
  SYNC_UPLOAD_SINGLE_RECORD_TOO_LARGE_ES,
} from '../domain/offlineSyncPayloadSizeGuard.ts';

/** Shown when the server accepted the payload but the local "uploaded" mark could not be saved. */
const OFFLINE_UPLOAD_PERSIST_WARNING =
  'Los registros se enviaron al servidor, pero no se pudo guardar la marca local de subida; podrían reenviarse en la próxima sincronización.';

export class OfflineSyncApplicationService implements OfflineSyncPort {
  /** Constructs this instance. */
  constructor(
    private readonly puntosEntrega: OfflineSyncPuntosEntregaExportPort,
    private readonly redencionBono: OfflineSyncRedencionExportPort,
    private readonly resolveUrl: () => string | null,
    private readonly poster: (
      url: string,
      body: OfflineSyncPayload,
      onProgress?: (loaded: number, total: number) => void,
    ) => Promise<OfflineSyncUploadResult>,
    private readonly assertReachableBeforeSync: (url: string) => Promise<void> = async () => {},
    private readonly pdfResolver: SyncPdfResolver = async () => '',
    private readonly persistOfflineUploadSuccess?: (batch: OfflineUploadPersistBatch) => void | Promise<void>,
  ) {}

  private isPendingOfflineUpload(at: string | null | undefined): boolean {
    if (at == null) return true;
    return String(at).trim().length === 0;
  }

  resolveUploadUrl(): string | null {
    return this.resolveUrl();
  }

  getExportRecordCounts(): { puntosEntrega: number; redenciones: number } {
    return {
      puntosEntrega: this.puntosEntrega.countPendingOfflineExport(),
      redenciones: this.redencionBono.countPendingOfflineExport(),
    };
  }

  async collectPayload(onProgress?: (p: OfflineSyncProgress) => void): Promise<OfflineSyncExportBody> {
    return this.collectExportBody({ pendingOnly: true }, onProgress);
  }

  async buildFullWireExportPayload(onProgress?: (p: OfflineSyncProgress) => void): Promise<OfflineSyncPayload> {
    const exportBody = await this.collectExportBody({ pendingOnly: false }, onProgress);
    const idempotencyKey = createCorrelationId();
    return buildOfflineSyncWirePayload(
      exportBody,
      idempotencyKey,
      this.puntosEntrega,
      this.redencionBono,
      this.pdfResolver,
    );
  }

  private async collectExportBody(
    options: { pendingOnly: boolean },
    onProgress?: (p: OfflineSyncProgress) => void,
  ): Promise<OfflineSyncExportBody> {
    const PAGE_SIZE = 200;
    const puntosEntrega: OfflineSyncExportBody['puntosEntrega'] = [];

    let page = 1;
    let catalogTotal = 0;

    for (;;) {
      const pageResult = this.puntosEntrega.listPuntosEntrega({ page, pageSize: PAGE_SIZE });
      if (page === 1) catalogTotal = pageResult.total;

      if (pageResult.items.length === 0) break;

      for (const item of pageResult.items) {
        const detail = this.puntosEntrega.getPuntoEntrega(item.id);
        if (
          detail &&
          (!options.pendingOnly || this.isPendingOfflineUpload(detail.offlineUploadedAt))
        ) {
          puntosEntrega.push(detail);
        }
        onProgress?.({
          phase: 'collecting_puntos',
          loaded: puntosEntrega.length,
          total: catalogTotal,
        });
      }

      if (pageResult.items.length < PAGE_SIZE) break;

      page += 1;
      if (page % 2 === 0) await Promise.resolve();
    }

    const lista = this.redencionBono.listRedenciones();
    const redenciones: OfflineSyncRedencionBundle[] = [];

    let done = 0;
    for (const row of lista) {
      const header = this.redencionBono.getRedencion(row.id);
      if (!header) {
        done += 1;
        onProgress?.({ phase: 'collecting_redenciones', loaded: done, total: lista.length });
        continue;
      }
      if (options.pendingOnly && !this.isPendingOfflineUpload(header.offlineUploadedAt)) {
        done += 1;
        onProgress?.({ phase: 'collecting_redenciones', loaded: done, total: lista.length });
        continue;
      }
      const participantes = this.redencionBono.listParticipantesForRedencion(row.id);
      redenciones.push({ header, participantes });
      done += 1;
      onProgress?.({ phase: 'collecting_redenciones', loaded: done, total: lista.length });
      if (done % 8 === 0) await Promise.resolve();
    }

    return {
      exportedAt: new Date().toISOString(),
      puntosEntrega,
      redenciones,
    };
  }

  async uploadPayload(
    url: string,
    payload: OfflineSyncPayload,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<OfflineSyncUploadResult> {
    return this.poster(url, payload, onProgress);
  }

  async runFullSync(
    onProgress: (p: OfflineSyncProgress) => void,
  ): Promise<
    Result<
      {
        puntosEntregaCount: number;
        redencionesCount: number;
        warnings: readonly string[];
        idSync: number | null;
        yearPostulacion: number | null;
        idempotencyKey: string;
      },
      string
    >
  > {
    const url = this.resolveUploadUrl();
    if (!url) {
      return fail('Falta configurar el destino de sincronización: defina SYNC_UPLOAD_BASE_URL y SYNC_UPLOAD_PATH en el entorno de compilación.');
    }
    try {
      onProgress({ phase: 'checking_network' });
      await this.assertReachableBeforeSync(url);
      const exportBody = await this.collectPayload(onProgress);
      const counts = {
        puntosEntregaCount: exportBody.puntosEntrega.length,
        redencionesCount: exportBody.redenciones.length,
      };

      onProgress({ phase: 'uploading' });
      const uploadOutcome = await this.uploadExportBody(url, exportBody, onProgress);
      onProgress({
        phase: 'success',
        ...counts,
        warnings: uploadOutcome.warnings,
        idSync: uploadOutcome.idSync,
        yearPostulacion: uploadOutcome.yearPostulacion,
        idempotencyKey: uploadOutcome.idempotencyKey,
      });
      return ok({
        ...counts,
        warnings: uploadOutcome.warnings,
        idSync: uploadOutcome.idSync,
        yearPostulacion: uploadOutcome.yearPostulacion,
        idempotencyKey: uploadOutcome.idempotencyKey,
      });
    } catch (e) {
      return fail(formatSyncFailureUserMessage(e));
    }
  }

  private syncUploadMaxPayloadBytes(): number {
    return AppConfig.syncUploadMaxPayloadBytes();
  }

  private async persistUploadBatch(batch: OfflineUploadPersistBatch): Promise<void> {
    try {
      await this.persistOfflineUploadSuccess?.(batch);
    } catch {
      // Server already accepted the payload: surface as warning, not failure (retry would duplicate).
      throw new Error(OFFLINE_UPLOAD_PERSIST_WARNING);
    }
  }

  private async uploadWirePayload(
    url: string,
    payload: OfflineSyncPayload,
    persistBatch: OfflineUploadPersistBatch,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<{ warnings: string[]; idSync: number | null; yearPostulacion: number | null }> {
    const upload = await this.uploadPayload(url, payload, onProgress);
    const warnings = [...upload.warnings];
    try {
      await this.persistUploadBatch(persistBatch);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      warnings.push(msg || OFFLINE_UPLOAD_PERSIST_WARNING);
    }
    return { warnings, idSync: upload.idSync, yearPostulacion: upload.yearPostulacion };
  }

  private async uploadExportBody(
    url: string,
    exportBody: OfflineSyncExportBody,
    onProgress: (p: OfflineSyncProgress) => void,
  ): Promise<{
    warnings: string[];
    idSync: number | null;
    yearPostulacion: number | null;
    idempotencyKey: string;
  }> {
    const maxBytes = this.syncUploadMaxPayloadBytes();
    const idempotencyKey = createCorrelationId();
    const fullPayload = await buildOfflineSyncWirePayload(
      exportBody,
      idempotencyKey,
      this.puntosEntrega,
      this.redencionBono,
      this.pdfResolver,
    );

    if (!exceedsSyncUploadMaxPayloadBytes(fullPayload, maxBytes)) {
      const outcome = await this.uploadWirePayload(
        url,
        fullPayload,
        {
          puntoIds: exportBody.puntosEntrega.map((p) => p.id),
          redencionIds: exportBody.redenciones.map((r) => r.header.id),
          uploadedAtIso: new Date().toISOString(),
        },
        (loaded, total) => onProgress({ phase: 'uploading', loaded, total }),
      );
      return { ...outcome, idempotencyKey };
    }

    const warnings: string[] = [];
    let idSync: number | null = null;
    let yearPostulacion: number | null = null;
    const uploadedAtIso = new Date().toISOString();
    const totalBatches = exportBody.puntosEntrega.length + exportBody.redenciones.length;
    let batchIndex = 0;

    for (const pe of exportBody.puntosEntrega) {
      const miniBody: OfflineSyncExportBody = {
        exportedAt: new Date().toISOString(),
        puntosEntrega: [pe],
        redenciones: [],
      };
      const payload = await buildOfflineSyncWirePayload(
        miniBody,
        createCorrelationId(),
        this.puntosEntrega,
        this.redencionBono,
        this.pdfResolver,
      );
      if (exceedsSyncUploadMaxPayloadBytes(payload, maxBytes)) {
        throw new Error(SYNC_UPLOAD_SINGLE_RECORD_TOO_LARGE_ES);
      }
      batchIndex += 1;
      const outcome = await this.uploadWirePayload(
        url,
        payload,
        { puntoIds: [pe.id], redencionIds: [], uploadedAtIso },
        (loaded, total) =>
          onProgress({
            phase: 'uploading',
            loaded: batchIndex - 1 + loaded / Math.max(total, 1),
            total: totalBatches,
          }),
      );
      warnings.push(...outcome.warnings);
      idSync = outcome.idSync ?? idSync;
      yearPostulacion = outcome.yearPostulacion ?? yearPostulacion;
    }

    for (const rb of exportBody.redenciones) {
      const miniBody: OfflineSyncExportBody = {
        exportedAt: new Date().toISOString(),
        puntosEntrega: [],
        redenciones: [rb],
      };
      const payload = await buildOfflineSyncWirePayload(
        miniBody,
        createCorrelationId(),
        this.puntosEntrega,
        this.redencionBono,
        this.pdfResolver,
      );
      if (exceedsSyncUploadMaxPayloadBytes(payload, maxBytes)) {
        throw new Error(SYNC_UPLOAD_SINGLE_RECORD_TOO_LARGE_ES);
      }
      batchIndex += 1;
      const outcome = await this.uploadWirePayload(
        url,
        payload,
        { puntoIds: [], redencionIds: [rb.header.id], uploadedAtIso },
        (loaded, total) =>
          onProgress({
            phase: 'uploading',
            loaded: batchIndex - 1 + loaded / Math.max(total, 1),
            total: totalBatches,
          }),
      );
      warnings.push(...outcome.warnings);
      idSync = outcome.idSync ?? idSync;
      yearPostulacion = outcome.yearPostulacion ?? yearPostulacion;
    }

    if (warnings.length === 0 && batchIndex === 0) {
      throw new Error(SYNC_UPLOAD_PAYLOAD_TOO_LARGE_ES);
    }

    return { warnings, idSync, yearPostulacion, idempotencyKey };
  }
}
